import { ApolloQueryResult } from '@apollo/client';
import { GetStaticPaths, GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import { ReactElement } from 'react';
import { Storyblok, useStoryblok } from 'storyblok';

import { Layout } from '@/components';
import { FaqSearchProvider } from '@/components/cms/FaqSearch/FaqSearchContext';
import Footer from '@/components/global/Footer';
import Header from '@/components/global/Header';
import BaseSeo from '@/components/seo/BaseSeo';
import { serverApolloClient, useApolloClient } from '@/lib/auth/useApolloClient';
import { VERSION } from '@/lib/constants';
import { contextToRegionQuery } from '@/lib/regions';
import { getStoryBlokProps } from '@/lib/storyblok/storyblokGetStaticProps';
import { getStoryBlokNavLinks } from '@/lib/storyblok/storyblokNavLinks';
import { PageDocument, PageQuery, PageQueryVariables } from '@/saleor/api';

import DynamicComponent from '../storyblok/components/DynamicComponent';

const Page = ({
  story,
  preview,
  resolve_relations
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const sbStory = preview ? useStoryblok(story, preview, resolve_relations) : story;
  const globalLinks = getStoryBlokNavLinks(story);

  const {
    content: { headerTheme = '', footerTheme = '', SEO = {} },
    name = '',
    full_slug
  } = sbStory;

  return (
    <div>
      <BaseSeo
        title={SEO?.title}
        description={SEO?.description}
        ogDescription={SEO?.og_description}
        ogTitle={SEO?.og_title}
        image={SEO?.og_image}
        path={full_slug}
      />
      <Header navigation={globalLinks} headerTheme={headerTheme} pageName={name} />
      <main id="main">
        <FaqSearchProvider>
          {sbStory && sbStory.content && <DynamicComponent blok={sbStory.content} />}
        </FaqSearchProvider>
      </main>
      <Footer theme={footerTheme} navigation={globalLinks.footerLinks} />
    </div>
  );
};

export default Page;

export interface pathParams {
  channel: string;
  slug: string;
}

export const getStaticPaths: GetStaticPaths = async () => {
  // get all links from Storyblok
  const { data } = await Storyblok.get('cdn/links/');

  // get top level page slugs to build
  const paths = Object.keys(data.links).reduce((acc, link) => {
    const page = data.links[link];

    if (!page) return acc;

    const { is_folder: isFolder, slug } = page;

    const isHome = slug === 'home';
    const isFolderPage = slug.split('/').length > 1;

    if (isFolder || isHome || isFolderPage) return acc;

    return [...acc, { params: { slug } }];
  }, []);

  return {
    paths,
    fallback: 'blocking'
  };
};

export const getStaticProps = async (context: GetStaticPropsContext) => {
  const pageSlug = context.params?.slug?.toString()!;
  const preview = context.preview || false;
  const resolveRelations = 'page.globalHeader,page.globalFooter,GlobalBlock.content';
  const story = await getStoryBlokProps({ pageSlug, context, VERSION, resolveRelations });

  if (story?.notFound) {
    return story;
  }

  const response: ApolloQueryResult<PageQuery> = await serverApolloClient.query<
    PageQuery,
    PageQueryVariables
  >({
    query: PageDocument,
    variables: {
      slug: pageSlug,
      locale: contextToRegionQuery(context).locale
    }
  });

  return {
    props: {
      page: response.data.page,
      preview,
      story: story || null,
      resolve_relations: resolveRelations
    },
    revalidate: 60 * 60 // value in seconds, how often ISR will trigger on the server
  };
};

Page.getLayout = function getLayout(page: ReactElement) {
  return <Layout>{page}</Layout>;
};
